import { Atom, atom, Getter } from "jotai"
import { atomWithDefault } from "jotai/utils"

import type { BaseRecord, CellConfig } from "./types"
import { getRowById } from "./atoms"

function serializeKey(rowId: string, columnId: string) {
  return [rowId, columnId].join("/")
}

function deserializeKey(key: string) {
  return key.split("/")
}

export function createCellStore<R extends BaseRecord = {}>() {
  const atoms = new Map<string, Atom<any>>()
  const cfgs = new Map<string, CellConfig>()

  //
  const EMPTY = Symbol()
  const emptyAtom = atom<typeof EMPTY>(EMPTY)

  const setCellConfig = (columnId: string, cfg: CellConfig) => {
    cfgs.set(columnId, cfg)
  }

  const isEmpty = (x: any) => x === EMPTY

  function makeGetCellGetter(get: Getter, rowId: string) {
    return function getCellValue<C>(columnId: string, or: C): C {
      const atom = getCell(rowId, columnId)
      return get(atom)
    }
  }

  const initialize = (rowId: string, columnId: string): Atom<any> => {
    const c = cfgs.get(columnId)
    if (!c) return emptyAtom
    if (c.type === "display") return emptyAtom
    if (c.type === "derived") {
      return atom<any>((get) => {
        const getCellValue = makeGetCellGetter(get, rowId)
        return c.value(getCellValue)
      })
    } else {
      return atomWithDefault<any>((get) => {
        const initialRow = getRowById<R>(get, rowId)
        return c.value(initialRow)
      })
    }
  }

  function getCell(rowId: string, columnId: string) {
    const key = serializeKey(rowId, columnId)
    if (atoms.get(key)) {
      return atoms.get(key)!
    } else {
      const newItem = initialize(rowId, columnId)
      atoms.set(key, newItem)
      return newItem
    }
  }

  const getRowCells = (get: Getter, rowId: string) => {
    const entries: [string, any][] = []
    for (const key of atoms.keys()) {
      const [_rowId, columnId] = deserializeKey(key)
      if (_rowId === rowId) {
        const cfg = cfgs.get(columnId)
        if (cfg?.type === "writable" || cfg?.type === "derived") {
          const path = cfg.path
          const value = get(getCell(rowId, columnId))
          entries.push([path, value])
        }
      }
    }

    return entries
  }

  return { getCell, getRowCells, isEmpty, setCellConfig }
}
